import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Mail } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { createAdmin, fetchAdmins } from '../../store/users/usersSlice';
import { Button, Input, useToast } from '../../components/common';
import { emailNotificationService } from '../../services/email';
import { User } from '../../types';

// Generate a secure random password
const generatePassword = (length: number = 12): string => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%^&*';
  const allChars = uppercase + lowercase + numbers + special;

  let password = '';
  // Ensure at least one of each type
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const adminSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phoneNumber: z.string().min(10, 'Phone number must be at least 10 characters').optional().or(z.literal('')),
  title: z.string().optional(),
});

type AdminFormData = z.infer<typeof adminSchema>;

export const AddAdminPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isLoading, error, admins } = useAppSelector((state) => state.users);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [isSendingEmails, setIsSendingEmails] = useState(false);

  // Fetch all admins on page load for notifications
  useEffect(() => {
    dispatch(fetchAdmins());
  }, [dispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminFormData>({
    resolver: zodResolver(adminSchema),
  });

  const onSubmit = async (data: AdminFormData) => {
    // Auto-generate password
    const generatedPassword = generatePassword();

    const adminData = {
      ...data,
      password: generatedPassword,
      phoneNumber: data.phoneNumber || undefined,
    };

    const result = await dispatch(createAdmin(adminData));

    if (createAdmin.fulfilled.match(result)) {
      showToast(`Admin "${data.firstName} ${data.lastName}" has been created. Login details sent to their email.`, 'success');

      // Send email notifications in the background
      setIsSendingEmails(true);

      // Get list of existing admins (excluding the newly created one)
      const existingAdmins = admins.filter((admin: User) => admin.email !== data.email);

      // Send notifications to all existing admins
      if (existingAdmins.length > 0 && currentUser) {
        emailNotificationService.notifyAdminsOfNewAdmin(existingAdmins, {
          newAdmin: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            title: data.title,
          },
          addedBy: {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            email: currentUser.email,
          },
        }).catch((err) => console.error('Failed to send admin notifications:', err));
      }

      // Send welcome email to the new admin with their credentials
      if (currentUser) {
        emailNotificationService.sendWelcomeEmailToAdmin({
          admin: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            title: data.title,
          },
          tempPassword: generatedPassword,
          addedBy: {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
          },
        }).catch((err) => console.error('Failed to send welcome email:', err));
      }

      setIsSendingEmails(false);
      navigate('/admin/users');
    }
  };

  return (
    <div>
      <button
        className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-slate-600 hover:text-slate-900 mb-4 sm:mb-6 transition-colors"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        Back to Users
      </button>

      <div className="max-w-md mx-auto bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">Add New Admin</h1>

        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm flex items-start gap-2">
          <Mail size={18} className="flex-shrink-0 mt-0.5" />
          <span>A secure password will be auto-generated and sent to the admin's email address.</span>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <Input
              id="firstName"
              label="First Name *"
              placeholder="Enter first name"
              error={errors.firstName?.message}
              {...register('firstName')}
            />

            <Input
              id="lastName"
              label="Last Name *"
              placeholder="Enter last name"
              error={errors.lastName?.message}
              {...register('lastName')}
            />
          </div>

          <Input
            id="email"
            type="email"
            label="Email *"
            placeholder="Enter email address"
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            id="phoneNumber"
            type="tel"
            label="Phone Number"
            placeholder="e.g. +233 24 123 4567"
            error={errors.phoneNumber?.message}
            {...register('phoneNumber')}
          />

          <Input
            id="title"
            label="Title"
            placeholder="e.g. Regional Coordinator, Zone Leader"
            error={errors.title?.message}
            {...register('title')}
          />

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 sm:pt-4">
            <Button type="button" variant="secondary" onClick={() => navigate(-1)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" isLoading={isLoading || isSendingEmails} className="w-full sm:w-auto">
              {isSendingEmails ? 'Sending Notifications...' : 'Create Admin'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
